import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, IconButton, Tooltip } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDispatch } from "react-redux";
import {
  LuTrendingUp,
  LuUsers,
  LuBriefcase,
  LuHeartHandshake,
  LuClock,
  LuInfo,
} from "react-icons/lu";
import { dashboard } from "../../redux/dashboard/dashboardSlice";
import { formatAmount } from "../../utils";
import { OFF_WHITE } from "../../constants/layout";
import AppLoader from "../../components/AppLoader/AppLoader";
import authService from "../../redux/auth/authService";
import {
  getAdminHomeRoute,
  SHOW_ADMIN_ANALYTICS_DASHBOARD,
} from "../../constants/adminAccess";

const BLACK = "#000000";
const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const ICON_BLUE = "#1E3A5F";

/** Distinct palette — easy to read on white, not all blue */
const INVESTMENT_COLORS: Record<string, string> = {
  MF: "#1E3A5F",
  BOND: "#E07A2F",
  EQUITY: "#2E9B6A",
  AIF: "#7C5CBF",
  PMS: "#D4A017",
  FD: "#3B82F6",
};

const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const PROFILE_FONT = '"Inter", sans-serif';

const formatAmountDisplay = (amount: number) => {
  if (amount >= 1e7) {
    return `₹${(amount / 1e7).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} Cr`;
  }
  if (amount >= 1e5) {
    return `₹${(amount / 1e5).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} L`;
  }
  return formatAmount(amount);
};

type InvestmentApi = {
  fdInvestment?: number;
  mfInvestment?: number;
  equityInvestment?: number;
  aifInvestment?: number;
  pmsInvestment?: number;
  bondInvestment?: number;
  totalInvestment?: number;
};

type UsersApi = {
  totalUsers?: number;
  totalActiveRm?: number;
  totalActivePartners?: number;
};

type PendingRequestsApi = {
  fd?: number;
  mutualFunds?: number;
  BOND?: number;
  PMS?: number;
  AIF?: number;
};

type AdminDashboardData = {
  investment?: InvestmentApi;
  users?: UsersApi;
  pendingRequests?: PendingRequestsApi;
};

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
  p: { xs: 2, md: 2.5 },
  height: "100%",
};

const StatCard = ({
  label,
  value,
  icon: Icon,
  isCurrency = false,
  infoTooltip,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  isCurrency?: boolean;
  infoTooltip?: string;
}) => (
  <Box sx={cardSx}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        mb: isCurrency ? 1 : 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: 13,
          fontWeight: 500,
          color: DARK_NAVY_LIGHT,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          bgcolor: "rgba(30, 58, 95, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={22} color={ICON_BLUE} strokeWidth={2} />
      </Box>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: isCurrency ? { xs: 18, sm: 20, md: 22 } : { xs: 22, md: 26 },
          fontWeight: 700,
          color: BLACK,
          lineHeight: 1.25,
          whiteSpace: isCurrency ? "nowrap" : "normal",
        }}
      >
        {value}
      </Typography>
      {infoTooltip ? (
        <Tooltip
          title={
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, p: 0.5 }}>
              {infoTooltip}
            </Typography>
          }
          arrow
          placement="top"
        >
          <IconButton
            size="small"
            aria-label="View full investment amount"
            sx={{
              p: 0.25,
              color: ICON_BLUE,
              "&:hover": { bgcolor: "rgba(30, 58, 95, 0.08)" },
            }}
          >
            <LuInfo size={18} strokeWidth={2} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  </Box>
);

const buildInvestmentSlices = (investment?: InvestmentApi) => {
  const items = [
    { key: "MF", label: "Mutual Funds", value: investment?.mfInvestment || 0 },
    { key: "BOND", label: "Bonds", value: investment?.bondInvestment || 0 },
    { key: "EQUITY", label: "Equity", value: investment?.equityInvestment || 0 },
    { key: "AIF", label: "AIF", value: investment?.aifInvestment || 0 },
    { key: "PMS", label: "PMS", value: investment?.pmsInvestment || 0 },
    { key: "FD", label: "Fixed Deposit", value: investment?.fdInvestment || 0 },
  ].filter((item) => item.value > 0);

  const total =
    investment?.totalInvestment ||
    items.reduce((sum, item) => sum + item.value, 0);

  return { items, total };
};

const AdminAnalyticsView = ({ data }: { data: AdminDashboardData }) => {
  const { investment, users, pendingRequests } = data;
  const { items, total } = useMemo(
    () => buildInvestmentSlices(investment),
    [investment]
  );

  const pieData = items.map((item) => ({
    id: item.key,
    label: item.key,
    value: item.value,
    color: INVESTMENT_COLORS[item.key] || ICON_BLUE,
  }));

  const pendingFd = pendingRequests?.fd ?? 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Total Investment"
            value={formatAmountDisplay(investment?.totalInvestment || 0)}
            infoTooltip={formatAmount(investment?.totalInvestment || 0)}
            icon={LuTrendingUp}
            isCurrency
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Total Users"
            value={users?.totalUsers ?? 0}
            icon={LuUsers}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Active RMs"
            value={users?.totalActiveRm ?? 0}
            icon={LuBriefcase}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Active Partners"
            value={users?.totalActivePartners ?? 0}
            icon={LuHeartHandshake}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 18,
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              Investment Distribution
            </Typography>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 13,
                color: DARK_NAVY_LIGHT,
                mb: 2,
              }}
            >
              Breakdown by product type
            </Typography>

            {pieData.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "center", md: "flex-start" },
                  gap: { xs: 3, md: 4 },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 300,
                    height: 300,
                    flexShrink: 0,
                  }}
                >
                  <PieChart
                    series={[
                      {
                        data: pieData,
                        innerRadius: 88,
                        outerRadius: 130,
                        paddingAngle: 1.5,
                        cornerRadius: 3,
                        cx: 150,
                        cy: 150,
                      },
                    ]}
                    width={300}
                    height={300}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    sx={{
                      "& .MuiChartsLegend-root": { display: "none" },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      width: 130,
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 11,
                        fontWeight: 500,
                        color: DARK_NAVY_LIGHT,
                        lineHeight: 1.3,
                        mb: 0.5,
                      }}
                    >
                      Total Investment
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 11,
                        fontWeight: 700,
                        color: BLACK,
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {formatAmount(total)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                  <Grid
                    container
                    sx={{
                      borderBottom: "1px solid #E8E8E8",
                      pb: 1,
                      mb: 0.5,
                    }}
                  >
                    <Grid item xs={4}>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 12,
                          fontWeight: 600,
                          color: DARK_NAVY_LIGHT,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        Instrument
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 12,
                          fontWeight: 600,
                          color: DARK_NAVY_LIGHT,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          textAlign: "right",
                        }}
                      >
                        Amount (₹)
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 12,
                          fontWeight: 600,
                          color: DARK_NAVY_LIGHT,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          textAlign: "right",
                        }}
                      >
                        %
                      </Typography>
                    </Grid>
                  </Grid>

                  {items.map((item) => {
                    const pct =
                      total > 0 ? ((item.value / total) * 100).toFixed(2) : "0.00";
                    const segmentColor = INVESTMENT_COLORS[item.key] || ICON_BLUE;
                    return (
                      <Grid
                        container
                        key={item.key}
                        sx={{
                          py: 1.25,
                          borderBottom: "1px solid #F5F5F5",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={4}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: segmentColor,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: PROFILE_FONT,
                                fontSize: 14,
                                fontWeight: 600,
                                color: DARK_NAVY,
                              }}
                            >
                              {item.key}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 13,
                              fontWeight: 500,
                              color: BLACK,
                              textAlign: "right",
                              lineHeight: 1.4,
                            }}
                          >
                            {formatAmount(item.value)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 13,
                              fontWeight: 500,
                              color: DARK_NAVY_LIGHT,
                              textAlign: "right",
                            }}
                          >
                            {pct}%
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT, py: 4, textAlign: "center" }}>
                No investment data available
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 18,
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              Pending Requests
            </Typography>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 13,
                color: DARK_NAVY_LIGHT,
                mb: 3,
              }}
            >
              Awaiting action
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderRadius: "12px",
                bgcolor: OFF_WHITE,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    bgcolor: "rgba(30, 58, 95, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LuClock size={20} color={ICON_BLUE} strokeWidth={2} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    color: DARK_NAVY,
                  }}
                >
                  FD Pending Requests
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 28,
                  fontWeight: 700,
                  color: ICON_BLUE,
                }}
              >
                {pendingFd}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const AnalyticsDash = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<number | null>(null);
  const [adminData, setAdminData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userTypeString = localStorage.getItem("userType");
    setUserType(userTypeString ? parseInt(userTypeString, 10) : null);
  }, []);

  useEffect(() => {
    if (userType === 4 && !SHOW_ADMIN_ANALYTICS_DASHBOARD) {
      navigate(getAdminHomeRoute(authService.getAumUserId()), { replace: true });
    }
  }, [userType, navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (userType !== 4) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await dispatch(dashboard({ userType }));
        if (response?.payload?.data) {
          setAdminData(response.payload.data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
      } finally {
        setLoading(false);
      }
    };

    if (userType !== null) {
      fetchDashboardData();
    }
  }, [dispatch, userType]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={loading} />
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            color: DARK_NAVY,
            mb: 0.5,
          }}
        >
          Analytics Dashboard
        </Typography>
        <Typography sx={{ fontSize: 14, color: DARK_NAVY_LIGHT, mb: 3 }}>
          Overview of investments, users, and pending requests
        </Typography>

        {userType === 4 && !loading && adminData ? (
          <AdminAnalyticsView data={adminData} />
        ) : userType === 4 && !loading ? (
          <Typography sx={{ color: DARK_NAVY_LIGHT, py: 6, textAlign: "center" }}>
            No dashboard data available
          </Typography>
        ) : !loading ? (
          <Typography sx={{ color: DARK_NAVY_LIGHT, py: 6, textAlign: "center" }}>
            Analytics dashboard is available for admin users
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default AnalyticsDash;
